import { useLiveQuery } from 'dexie-react-hooks';
import { db } from './database';

export function useAppSettings() {
  return useLiveQuery(() => db.settings.get('app'), []);
}

export function useProducts(options: { activeOnly?: boolean } = {}) {
  return useLiveQuery(async () => {
    const products = options.activeOnly
      ? await db.products.filter((product) => product.active).toArray()
      : await db.products.toArray();
    return [...products].sort((left, right) => left.sortOrder - right.sortOrder);
  }, [options.activeOnly]);
}

export function useWaterHardness(options: { activeOnly?: boolean } = {}) {
  return useLiveQuery(async () => {
    const values = options.activeOnly
      ? await db.waterHardness.filter((hardness) => hardness.active).toArray()
      : await db.waterHardness.toArray();
    return [...values].sort((left, right) => left.sortOrder - right.sortOrder);
  }, [options.activeOnly]);
}

export function useContainers(options: { activeOnly?: boolean } = {}) {
  return useLiveQuery(async () => {
    const values = options.activeOnly
      ? await db.containers.filter((container) => container.active).toArray()
      : await db.containers.toArray();
    return [...values].sort((left, right) => left.sortOrder - right.sortOrder);
  }, [options.activeOnly]);
}

export function useMeasuringCups(options: { activeOnly?: boolean } = {}) {
  return useLiveQuery(async () => {
    const values = options.activeOnly
      ? await db.measuringCups.filter((cup) => cup.active).toArray()
      : await db.measuringCups.toArray();
    return [...values].sort((left, right) => left.sortOrder - right.sortOrder);
  }, [options.activeOnly]);
}

export function useDosageTables(productId?: string) {
  return useLiveQuery(
    () =>
      productId
        ? db.dosageTables.where('productId').equals(productId).toArray()
        : db.dosageTables.toArray(),
    [productId],
  );
}

export function useConversion(productId?: string) {
  return useLiveQuery(
    () => (productId ? db.conversionConfigurations.get(productId) : undefined),
    [productId],
  );
}

export function useTemperatureGuidance(productId?: string) {
  return useLiveQuery(async () => {
    const values = productId
      ? await db.temperatureGuidance.where('productId').equals(productId).toArray()
      : await db.temperatureGuidance.toArray();
    return [...values].sort((left, right) => left.sortOrder - right.sortOrder);
  }, [productId]);
}

export function useRecommendationRules() {
  return useLiveQuery(async () => {
    const values = await db.recommendationRules.toArray();
    return [...values].sort((left, right) => right.priority - left.priority);
  }, []);
}

export function useStainGuides(options: { activeOnly?: boolean } = {}) {
  return useLiveQuery(async () => {
    const values = options.activeOnly
      ? await db.stainGuides.filter((guide) => guide.active).toArray()
      : await db.stainGuides.toArray();
    return [...values].sort((left, right) => left.sortOrder - right.sortOrder);
  }, [options.activeOnly]);
}

export function useTeacherData() {
  return useLiveQuery(async () => {
    const [
      settings,
      products,
      dosageTables,
      waterHardness,
      containers,
      measuringCups,
      conversionConfigurations,
      calibrationMeasurements,
      temperatureGuidance,
      recommendationRules,
      stainGuides,
    ] = await Promise.all([
      db.settings.toArray(),
      db.products.toArray(),
      db.dosageTables.toArray(),
      db.waterHardness.toArray(),
      db.containers.toArray(),
      db.measuringCups.toArray(),
      db.conversionConfigurations.toArray(),
      db.calibrationMeasurements.toArray(),
      db.temperatureGuidance.toArray(),
      db.recommendationRules.toArray(),
      db.stainGuides.toArray(),
    ]);
    return {
      settings,
      products,
      dosageTables,
      waterHardness,
      containers,
      measuringCups,
      conversionConfigurations,
      calibrationMeasurements,
      temperatureGuidance,
      recommendationRules,
      stainGuides,
    };
  }, []);
}
